import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X, FileText, Loader2, CheckCircle2, AlertCircle, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/Button.jsx';

const ACCEPT_TYPES = [
    'image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/heic', 'image/heif',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',  // xlsx
    'application/vnd.ms-excel',  // xls
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',  // docx
    'application/pdf',
    'text/csv', 'text/tab-separated-values', 'text/plain',
].join(',');

/**
 * ImportDirectoryDialog — 通訊錄匯入流程 UI
 *
 * Props:
 *   open, onClose
 *   hostId
 *   job          - 來自 useDirectoryImportJob().job
 *   loading      - hook loading
 *   onStart(file)
 *   onConfirm(contacts)
 *   onDismiss()
 *   onDone()     - 寫完後 parent 重新 load directory
 */
export function ImportDirectoryDialog({
    open, onClose, hostId,
    job, loading, onStart, onConfirm, onDismiss, onDone,
    existingDirectory = []
}) {
    const { t } = useTranslation('directory');
    // 用 ext 建索引，preview 時標 新/更
    const existingByExt = useMemo(() => {
        const m = new Map();
        for (const e of existingDirectory) {
            const k = String(e.ext || '').trim().toLowerCase();
            if (k) m.set(k, e);
        }
        return m;
    }, [existingDirectory]);

    const [file, setFile] = useState(null);
    const [editing, setEditing] = useState([]);   // mutable copy of job.contacts for preview edit
    const fileRef = useRef(null);

    // 把 job.contacts 同步到 editing（只在首次或 jobId 變化時）
    const previewKey = job?.id + ':' + job?.status;
    useEffect(() => {
        if (job?.status === 'preview_ready') {
            setEditing(job.contacts.map(c => ({
                ...c,
                aliases: Array.isArray(c.aliases) ? c.aliases : [],
                services: Array.isArray(c.services) ? c.services : []
            })));
        } else if (job?.status === 'done' || job?.status === 'error') {
            // keep editing as-is for display
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewKey]);

    if (!open) return null;

    const status = job?.status || (file ? 'ready_to_start' : 'idle');

    return (
        <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm grid place-items-center px-4">
            <div className="bg-cream-50 rounded-2xl shadow-[0_20px_60px_-20px_rgba(28,26,23,0.3)] w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200 bg-white">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-400">
                            tenant · {hostId}
                        </div>
                        <h2 className="heading-display text-xl font-semibold text-ink-900 mt-0.5">
                            {t('import.title')}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        title={['parsing', 'committing'].includes(status) ? t('import.closeBackground') : t('common:actions.close')}
                        className="size-9 inline-flex items-center justify-center text-ink-500 hover:text-ink-900 hover:bg-cream-100 rounded-md transition"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {status === 'idle' && (
                        <IdleView fileRef={fileRef} onPick={(f) => setFile(f)} />
                    )}
                    {status === 'ready_to_start' && (
                        <ReadyView file={file} onChange={() => setFile(null)} />
                    )}
                    {(status === 'created' || status === 'parsing') && (
                        <ProgressView job={job} title={t('import.parsing')} />
                    )}
                    {status === 'committing' && (
                        <ProgressView job={job} title={t('import.committing')} />
                    )}
                    {status === 'done' && (
                        <DoneView job={job} />
                    )}
                    {status === 'error' && (
                        <ErrorView job={job} />
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-cream-200 bg-white flex items-center justify-between gap-3">
                    <div className="text-xs text-ink-500">
                        {['parsing', 'committing'].includes(status) && (
                            <span className="inline-flex items-center gap-1.5">
                                <span className="size-1.5 rounded-full bg-accent-500 animate-pulse" />
                                {t('import.backgroundHint')}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {status === 'ready_to_start' && (
                            <>
                                <Button variant="ghost" onClick={() => setFile(null)}>{t('common:actions.cancel')}</Button>
                                <Button
                                    variant="accent"
                                    disabled={loading}
                                    onClick={async () => { await onStart(file); setFile(null); }}
                                >
                                    {loading ? t('import.uploading') : t('import.startImport')}
                                </Button>
                            </>
                        )}
                        {(status === 'done' || status === 'error') && (
                            <>
                                <Button variant="ghost" onClick={onDismiss}>{t('import.clearRecord')}</Button>
                                <Button variant="accent" onClick={() => { onDone?.(); onClose(); }}>
                                    {t('import.done')}
                                </Button>
                            </>
                        )}
                        {['parsing', 'committing'].includes(status) && (
                            <Button variant="ghost" onClick={onClose}>{t('import.runInBackground')}</Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function IdleView({ fileRef, onPick }) {
    const { t } = useTranslation('directory');
    const [dragOver, setDragOver] = useState(false);
    return (
        <div>
            <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    const f = e.dataTransfer.files?.[0];
                    if (f) onPick(f);
                }}
                onClick={() => fileRef.current?.click()}
                className={[
                    'border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition',
                    dragOver ? 'border-accent-500 bg-accent-50' : 'border-cream-300 hover:border-accent-400 hover:bg-cream-100/50'
                ].join(' ')}
            >
                <Upload size={42} strokeWidth={1.2} className="mx-auto text-cream-400 mb-4" />
                <div className="heading-display text-lg text-ink-900 mb-1">{t('idle.dropOrPick')}</div>
                <div className="text-sm text-ink-500">
                    {t('idle.supportPrefix')} <span className="text-ink-700">{t('idle.supportedFormats')}</span>
                </div>
                <input
                    ref={fileRef}
                    type="file"
                    accept={ACCEPT_TYPES}
                    hidden
                    onChange={(e) => { if (e.target.files?.[0]) onPick(e.target.files[0]); }}
                />
            </div>
            <div className="mt-6 text-xs text-ink-500 leading-relaxed">
                <div className="font-medium text-ink-700 mb-1">{t('idle.fieldsTitle')}</div>
                <ul className="list-disc pl-5 space-y-0.5">
                    <li>{t('idle.fieldNameExt')}</li>
                    <li>{t('idle.fieldDeptTitle')}</li>
                    <li>{t('idle.fieldNickname')}</li>
                    <li>{t('idle.fieldServices')}</li>
                </ul>
            </div>
            <div className="mt-4 flex items-start gap-2.5 px-3 py-2.5 bg-warn-100/50 border border-warn-500/30 rounded-md text-xs text-ink-700 leading-relaxed">
                <AlertTriangle size={14} className="text-warn-500 mt-0.5 shrink-0" strokeWidth={1.8} />
                <div>
                    <span className="font-medium">{t('idle.autoWriteWarn')}</span>
                    {' '}{t('idle.autoWriteDetail')}
                </div>
            </div>
        </div>
    );
}

function ReadyView({ file, onChange }) {
    const { t } = useTranslation('directory');
    return (
        <div className="border border-cream-200 rounded-xl bg-white px-5 py-6 flex items-center gap-4">
            <div className="size-12 rounded-md bg-cream-100 inline-flex items-center justify-center shrink-0">
                <FileText size={24} className="text-accent-700" strokeWidth={1.4} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-medium text-ink-900 truncate">{file.name}</div>
                <div className="text-xs text-ink-500 mt-0.5">
                    {(file.size / 1024).toFixed(1)} KB · {file.type || t('ready.unknownFormat')}
                </div>
            </div>
            <Button variant="ghost" onClick={onChange}>{t('ready.changeFile')}</Button>
        </div>
    );
}

function ProgressView({ job, title }) {
    return (
        <div className="py-6">
            <div className="flex items-center gap-3 mb-4">
                <Loader2 size={20} className="text-accent-500 animate-spin" />
                <div className="heading-display text-lg text-ink-900">{title}</div>
            </div>
            <div className="h-1.5 bg-cream-200 rounded-full overflow-hidden mb-5">
                <div
                    className="h-full bg-accent-500 transition-all duration-500"
                    style={{ width: `${job?.progress || 0}%` }}
                />
            </div>
            <LogView log={job?.log} />
        </div>
    );
}

function LogView({ log }) {
    if (!log?.length) return null;
    return (
        <div className="bg-ink-900 rounded-md p-3 max-h-40 overflow-y-auto font-mono text-[11px] text-cream-50 space-y-0.5">
            {log.map((entry, i) => (
                <div key={i} className="leading-relaxed">
                    <span className="text-cream-400 mr-2">{new Date(entry.ts).toLocaleTimeString('zh-TW', { hour12: false })}</span>
                    {entry.msg}
                </div>
            ))}
        </div>
    );
}

function PreviewView({ contacts, setContacts, job, existingByExt = new Map() }) {
    const { t } = useTranslation('directory');
    const stats = useMemo(() => {
        let willCreate = 0, willUpdate = 0, willSkip = 0;
        for (const c of contacts) {
            if (!c.name || !c.ext) { willSkip++; continue; }
            const key = String(c.ext).trim().toLowerCase();
            if (existingByExt.get(key)) willUpdate++; else willCreate++;
        }
        return { willCreate, willUpdate, willSkip };
    }, [contacts, existingByExt]);

    const update = (i, patch) => {
        setContacts(prev => prev.map((c, idx) => idx === i ? { ...c, ...patch } : c));
    };
    const remove = (i) => setContacts(prev => prev.filter((_, idx) => idx !== i));
    const add = () => setContacts(prev => [...prev, { name: '', ext: '', department: '', title: '', aliases: [], services: [] }]);

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="heading-display text-base font-semibold text-ink-900">
                        {t('preview.title', { count: contacts.length })}
                    </div>
                    <div className="text-xs text-ink-500 mt-1 inline-flex items-center gap-3">
                        <span className="inline-flex items-center gap-1">
                            <span className="size-1.5 rounded-full bg-success-500" />
                            <span className="text-ink-700 font-medium">{stats.willCreate}</span> {t('preview.willCreate')}
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <span className="size-1.5 rounded-full bg-accent-500" />
                            <span className="text-ink-700 font-medium">{stats.willUpdate}</span> {t('preview.willUpdate')}
                        </span>
                        {stats.willSkip > 0 && (
                            <span className="inline-flex items-center gap-1 text-warn-500">
                                <span className="size-1.5 rounded-full bg-warn-500" />
                                <span className="font-medium">{stats.willSkip}</span> {t('preview.willSkip')}
                            </span>
                        )}
                    </div>
                </div>
                <Button size="sm" variant="ghost" onClick={add}>
                    <Plus size={12} /> {t('preview.addRow')}
                </Button>
            </div>

            <div className="overflow-x-auto border border-cream-200 rounded-md bg-white">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-cream-200 bg-cream-50/50 text-[10px] uppercase tracking-wider text-ink-400 font-mono">
                            <th className="text-left px-2 py-2 font-medium w-14"></th>
                            <th className="text-left px-2 py-2 font-medium">{t('editor.nameLabel')}</th>
                            <th className="text-left px-2 py-2 font-medium w-24">{t('editor.extLabel')}</th>
                            <th className="text-left px-2 py-2 font-medium">{t('table.department')}</th>
                            <th className="text-left px-2 py-2 font-medium">{t('table.title')}</th>
                            <th className="text-left px-2 py-2 font-medium">{t('preview.nickname')}</th>
                            <th className="text-left px-2 py-2 font-medium">{t('preview.services')}</th>
                            <th className="w-8"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {contacts.map((c, i) => {
                            const missingRequired = !c.name || !c.ext;
                            const extKey = String(c.ext || '').trim().toLowerCase();
                            const existing = extKey ? existingByExt.get(extKey) : null;
                            const badgeKind = missingRequired ? 'skip' : (existing ? 'update' : 'create');
                            return (
                                <tr key={i} className={['border-b border-cream-100', missingRequired ? 'bg-warn-100/40' : ''].join(' ')}>
                                    <td className="px-2 py-0.5">
                                        <ActionBadge kind={badgeKind} existingName={existing?.name} />
                                    </td>
                                    <td className="px-1 py-0.5">
                                        <CellInput value={c.name} onChange={(v) => update(i, { name: v })} placeholder={t('preview.namePlaceholder')} />
                                    </td>
                                    <td className="px-1 py-0.5">
                                        <CellInput value={c.ext} onChange={(v) => update(i, { ext: v })} placeholder="ext" mono />
                                    </td>
                                    <td className="px-1 py-0.5">
                                        <CellInput value={c.department || ''} onChange={(v) => update(i, { department: v })} placeholder="—" />
                                    </td>
                                    <td className="px-1 py-0.5">
                                        <CellInput value={c.title || ''} onChange={(v) => update(i, { title: v })} placeholder="—" />
                                    </td>
                                    <td className="px-1 py-0.5">
                                        <CellInput
                                            value={(c.aliases || []).join('、')}
                                            onChange={(v) => update(i, { aliases: v.split(/[、,，]/).map(s => s.trim()).filter(Boolean) })}
                                            placeholder="—"
                                        />
                                    </td>
                                    <td className="px-1 py-0.5">
                                        <CellInput
                                            value={(c.services || []).join('、')}
                                            onChange={(v) => update(i, { services: v.split(/[、,，/]/).map(s => s.trim()).filter(Boolean) })}
                                            placeholder="—"
                                        />
                                    </td>
                                    <td className="px-1 py-0.5 text-center">
                                        <button
                                            onClick={() => remove(i)}
                                            className="text-ink-300 hover:text-danger-500 p-1"
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="mt-3">
                <LogView log={job?.log} />
            </div>
        </div>
    );
}

function ActionBadge({ kind, existingName }) {
    const { t } = useTranslation('directory');
    const map = {
        create: { label: t('badge.createLabel'), className: 'bg-success-100 text-success-500', title: t('badge.createTitle') },
        update: { label: t('badge.updateLabel'), className: 'bg-accent-50 text-accent-700', title: existingName ? t('badge.updateTitleNamed', { name: existingName }) : t('badge.updateTitle') },
        skip: { label: t('badge.skipLabel'), className: 'bg-warn-100 text-warn-500', title: t('badge.skipTitle') },
    };
    const cfg = map[kind] || map.skip;
    return (
        <span
            title={cfg.title}
            className={['inline-block px-1.5 py-0.5 text-[10px] font-medium rounded', cfg.className].join(' ')}
        >
            {cfg.label}
        </span>
    );
}

function CellInput({ value, onChange, placeholder, mono }) {
    return (
        <input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={[
                'w-full px-2 py-1.5 text-sm bg-transparent border border-transparent rounded',
                'hover:border-cream-300 focus:border-accent-500 focus:bg-white focus:outline-none',
                mono ? 'font-mono tabular-nums' : ''
            ].join(' ')}
        />
    );
}

function DoneView({ job }) {
    const { t } = useTranslation('directory');
    const r = job?.result || {};
    return (
        <div className="py-6 text-center">
            <CheckCircle2 size={48} strokeWidth={1.4} className="text-success-500 mx-auto mb-4" />
            <div className="heading-display text-2xl font-semibold text-ink-900 mb-2">{t('doneView.title')}</div>
            <div className="text-sm text-ink-500 mb-6">
                {t('doneView.createdPrefix')} <span className="text-ink-900 font-medium">{r.createdCount || 0}</span> {t('doneView.unit')} ·
                {t('doneView.updatedPrefix')} <span className="text-ink-900 font-medium">{r.updatedCount || 0}</span> {t('doneView.unit')}
                {r.skippedCount > 0 && <> · {t('doneView.skippedPrefix')} <span className="text-warn-500 font-medium">{r.skippedCount}</span> {t('doneView.unit')}</>}
            </div>
            {r.skipped?.length > 0 && (
                <div className="text-left bg-warn-100/40 border border-warn-500/30 rounded-md p-3 max-h-32 overflow-y-auto">
                    <div className="text-xs font-medium text-warn-500 mb-1">{t('doneView.skippedListTitle')}</div>
                    {r.skipped.map((s, i) => (
                        <div key={i} className="text-xs text-ink-700">
                            {s.contact?.name || t('doneView.noName')} / {s.contact?.ext || t('doneView.noExt')} — {s.reason}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ErrorView({ job }) {
    const { t } = useTranslation('directory');
    // job.stage='commit' = 解析已經過了，是「寫進通訊錄」這段失敗 → 標題不可再說解析失敗
    // （0806 回報：log 明明寫「已完成解析、抽出 497 筆」，標題卻是解析失敗）
    const title = job?.stage === 'commit' ? t('errorView.commitTitle') : t('errorView.title');
    return (
        <div className="py-6 text-center">
            <AlertCircle size={48} strokeWidth={1.4} className="text-danger-500 mx-auto mb-4" />
            <div className="heading-display text-xl font-semibold text-ink-900 mb-2">{title}</div>
            <div className="text-sm text-danger-500 mb-4 max-w-md mx-auto">{job?.error || t('errorView.unknownError')}</div>
            <LogView log={job?.log} />
        </div>
    );
}

/**
 * Floating chip — dialog 關掉但 job 仍在背景跑時顯示，點擊重開 dialog
 */
export function ImportJobIndicator({ job, onClick }) {
    const { t } = useTranslation('directory');
    if (!job) return null;
    const isActive = ['parsing', 'committing', 'preview_ready'].includes(job.status);
    if (!isActive) return null;
    const labelMap = {
        parsing: t('indicator.parsing'),
        preview_ready: t('indicator.previewReady'),
        committing: t('indicator.committing')
    };
    return (
        <button
            onClick={onClick}
            className="fixed bottom-6 right-6 z-40 bg-ink-900 text-cream-50 rounded-full pl-4 pr-5 py-3 shadow-[0_8px_24px_-8px_rgba(28,26,23,0.4)] flex items-center gap-2.5 hover:bg-ink-700 transition"
        >
            {job.status === 'preview_ready' ? (
                <span className="size-2 rounded-full bg-accent-300 animate-pulse" />
            ) : (
                <Loader2 size={14} className="animate-spin" />
            )}
            <span className="text-sm">{labelMap[job.status]}</span>
            <span className="text-[11px] font-mono text-cream-300">{job.progress || 0}%</span>
        </button>
    );
}
